import { dataLayer } from '../../../data/dataLayer'
import type { ModuleId } from '../../../content'
import type {
  AssessmentBlueprint,
  AssessmentAttempt,
  AssessmentReport,
  AssessmentDomainId,
  TierPerformance,
  FamilyPerformance,
  PracticeRecommendation,
  GrowthIndicator,
  SkillGroupedArea,
  ActionPlan,
} from '../../../assessment'
import { debug } from '../../../utils/logger'

/**
 * Seeds realistic assessment data directly into the dataLayer.
 * This is separate from the MultiAccountStateV4 because assessments
 * are stored in their own domain (D3).
 */

// ─── Demo Family Definitions ──────────────────────────────────
// Realistic family data per module for seeding rich reports

interface DemoFamily {
  id: string
  description: string
  skill: string
  accuracy: number
  preMastery: number
  postMastery: number
}

interface DemoModuleConfig {
  id: string
  title: string
  score: number
  levelCode: string
  estimatedTier: number
  expectedTier: number
  families: DemoFamily[]
  avgTimePerQuestion: number
}

function buildTierPerformance(
  totalQuestions: number,
  scorePercent: number,
): TierPerformance[] {
  // Simulate: easier tiers have higher accuracy, harder tiers lower
  const easyPct = Math.min(100, scorePercent + 15)
  const midPct = scorePercent
  const hardPct = Math.max(10, scorePercent - 20)

  const easyQ = Math.round(totalQuestions * 0.3)
  const midQ = Math.round(totalQuestions * 0.45)
  const hardQ = totalQuestions - easyQ - midQ

  return [
    {
      tierRange: '1-6',
      totalQuestions: easyQ,
      correctCount: Math.round((easyPct / 100) * easyQ),
      accuracy: easyPct,
    },
    {
      tierRange: '7-12',
      totalQuestions: midQ,
      correctCount: Math.round((midPct / 100) * midQ),
      accuracy: midPct,
    },
    {
      tierRange: '13-16',
      totalQuestions: hardQ,
      correctCount: Math.round((hardPct / 100) * hardQ),
      accuracy: hardPct,
    },
  ]
}

function buildFamilyPerformance(
  families: DemoFamily[],
  questionsPerFamily: number,
): FamilyPerformance[] {
  return families.map((f) => ({
    familyId: f.id,
    familyDescription: f.description,
    totalQuestions: questionsPerFamily,
    correctCount: Math.round((f.accuracy / 100) * questionsPerFamily),
    accuracy: f.accuracy,
    preMastery: f.preMastery,
    postMastery: f.postMastery,
    delta: f.postMastery - f.preMastery,
  }))
}

function buildGrowthIndicators(modules: DemoModuleConfig[]): GrowthIndicator[] {
  return modules.map((m) => {
    const avgAcc = m.score
    const avgPre =
      m.families.reduce((s, f) => s + f.preMastery, 0) /
      Math.max(1, m.families.length)

    let level: GrowthIndicator['level'] = 'onbekend'
    let emoji = '⚪'
    let label = 'Onbekend'
    let color = 'text-slate-400'

    if (avgPre >= 60 && avgAcc < 30) {
      level = 'actie_vereist'
      emoji = '🔴⚠'
      label = 'Actie vereist'
      color = 'text-rose-700'
    } else if (avgPre >= 50 && avgAcc < 40) {
      level = 'krimpt'
      emoji = '🔴↓'
      label = 'Krimpt'
      color = 'text-rose-600'
    } else if (avgAcc >= 80 && avgPre < 60) {
      level = 'groei_snel'
      emoji = '🟢↗'
      label = 'Ik groei snel'
      color = 'text-emerald-600'
    } else if (avgAcc >= 70) {
      level = 'gaat_goed'
      emoji = '🟢→'
      label = 'Het gaat goed'
      color = 'text-emerald-500'
    } else if (avgAcc >= 50) {
      level = 'ik_groei'
      emoji = '🟡↗'
      label = 'Ik groei'
      color = 'text-yellow-600'
    } else if (avgAcc >= 30) {
      level = 'let_op'
      emoji = '🟠'
      label = 'Let op'
      color = 'text-amber-600'
    } else {
      level = 'extra_aandacht'
      emoji = '🔴'
      label = 'Extra aandacht nodig'
      color = 'text-red-600'
    }

    return { moduleId: m.id as ModuleId, level, emoji, label, color }
  })
}

function buildSkillGroupedAreas(
  modules: DemoModuleConfig[],
  type: 'strong' | 'weak',
): SkillGroupedArea[] {
  return modules
    .map((m) => {
      const skills = m.families
        .filter((f) => (type === 'strong' ? f.accuracy >= 75 : f.accuracy < 60))
        .reduce(
          (acc, f) => {
            const existing = acc.find((s) => s.skillName === f.skill)
            if (existing) {
              existing.families.push({
                familyId: f.id,
                description: f.description,
                accuracy: f.accuracy,
                masteryLevel:
                  f.postMastery >= 80
                    ? 'gevorderd'
                    : f.postMastery >= 50
                      ? 'oefenaar'
                      : 'beginner',
              })
              existing.avgAccuracy =
                existing.families.reduce((s, fam) => s + fam.accuracy, 0) /
                existing.families.length
            } else {
              acc.push({
                skillName: f.skill,
                families: [
                  {
                    familyId: f.id,
                    description: f.description,
                    accuracy: f.accuracy,
                    masteryLevel:
                      f.postMastery >= 80
                        ? 'gevorderd'
                        : f.postMastery >= 50
                          ? 'oefenaar'
                          : 'beginner',
                  },
                ],
                avgAccuracy: f.accuracy,
              })
            }
            return acc
          },
          [] as SkillGroupedArea['skills'],
        )

      if (skills.length === 0) return null
      return {
        moduleId: m.id as ModuleId,
        moduleTitle: m.title,
        skills,
      }
    })
    .filter((x): x is SkillGroupedArea => x !== null)
}

function buildSuggestedPractice(
  modules: DemoModuleConfig[],
): PracticeRecommendation[] {
  const recs: PracticeRecommendation[] = []
  for (const m of modules) {
    for (const f of m.families) {
      if (f.accuracy < 50) {
        recs.push({
          familyId: f.id,
          familyDescription: f.description,
          currentMastery: f.postMastery,
          reason: 'weak_performance',
          priority: 1,
        })
      } else if (f.postMastery < 60) {
        recs.push({
          familyId: f.id,
          familyDescription: f.description,
          currentMastery: f.postMastery,
          reason: 'low_mastery',
          priority: 2,
        })
      } else if (f.accuracy >= 70 && f.postMastery < 85) {
        recs.push({
          familyId: f.id,
          familyDescription: f.description,
          currentMastery: f.postMastery,
          reason: 'high_potential',
          priority: 3,
        })
      }
    }
  }
  return recs.sort((a, b) => a.priority - b.priority).slice(0, 5)
}

function buildActionPlan(modules: DemoModuleConfig[]): ActionPlan {
  // Find weakest family for beginMet
  let weakestFamily: { mod: DemoModuleConfig; fam: DemoFamily } | null = null
  const mediumFamilies: { mod: DemoModuleConfig; fam: DemoFamily }[] = []
  let strongestModule: DemoModuleConfig | null = null

  for (const m of modules) {
    for (const f of m.families) {
      if (!weakestFamily || f.accuracy < weakestFamily.fam.accuracy) {
        weakestFamily = { mod: m, fam: f }
      }
      if (f.accuracy >= 50 && f.accuracy < 80) {
        mediumFamilies.push({ mod: m, fam: f })
      }
    }
    if (!strongestModule || m.score > strongestModule.score) {
      strongestModule = m
    }
  }

  return {
    beginMet: weakestFamily
      ? {
          moduleTitle: weakestFamily.mod.title,
          skillName: weakestFamily.fam.skill,
          familyDescription: weakestFamily.fam.description,
          moduleId: weakestFamily.mod.id as ModuleId,
        }
      : null,
    gaVerderMet: mediumFamilies.slice(0, 2).map((mf) => ({
      moduleTitle: mf.mod.title,
      skillName: mf.fam.skill,
      familyDescription: mf.fam.description,
      moduleId: mf.mod.id as ModuleId,
    })),
    uitdaging:
      strongestModule && strongestModule.score >= 75
        ? {
            moduleTitle: strongestModule.title,
            suggestion: `Probeer moeilijkere opgaven bij ${strongestModule.title} — je bent er klaar voor!`,
            moduleId: strongestModule.id as ModuleId,
          }
        : null,
  }
}

function buildRecommendation(
  scorePercent: number,
  weakFamilies: DemoFamily[],
): string {
  if (scorePercent >= 85)
    return 'Uitstekend! Je beheerst de stof goed. Focus op uitdagende onderwerpen om je kennis verder te verdiepen.'
  if (scorePercent >= 70) {
    if (weakFamilies.length > 0)
      return `Goed gedaan! Je bent op de goede weg. Focus op ${weakFamilies[0].description.toLowerCase()} om je resultaat verder te verbeteren.`
    return 'Goed gedaan! Je bent op de goede weg. Blijf oefenen om je kennis te versterken.'
  }
  if (scorePercent >= 55) {
    if (weakFamilies.length > 0)
      return `Blijf oefenen! Begin met ${weakFamilies[0].description.toLowerCase()} om snel vooruitgang te boeken.`
    return 'Blijf oefenen! Regelmatig oefenen helpt je om de stof beter te begrijpen.'
  }
  if (weakFamilies.length >= 2) {
    return `Geen zorgen, iedereen leert in zijn eigen tempo! Focus eerst op ${weakFamilies[0].description.toLowerCase()} en ${weakFamilies[1].description.toLowerCase()}. Neem de tijd en vraag hulp als je het nodig hebt.`
  }
  return 'Geen zorgen, iedereen leert in zijn eigen tempo! Blijf oefenen en vraag hulp als je het nodig hebt.'
}

export async function seedDemoAssessments(
  learnerIds: Record<string, string>,
  teacherId?: string,
  classroomId?: string,
): Promise<void> {
  const now = Date.now()
  const dayMs = 24 * 60 * 60 * 1000

  const { djayden, sophie, emma, noah, liam, noor } = learnerIds

  // Helper to create a full report with all fields populated
  const createReport = (
    attemptId: string,
    blueprintId: string,
    _learnerId: string,
    group: number,
    completedAt: number,
    scorePercent: number,
    totalQuestions: number,
    _domainId: string,
    modules: DemoModuleConfig[],
    overallLevelCode: string,
    overallTier: number,
    expectedTier: number,
  ): AssessmentReport => {
    const correctCount = Math.round((scorePercent / 100) * totalQuestions)
    const allFamilies = modules.flatMap((m) => m.families)
    const weakFamilies = allFamilies.filter((f) => f.accuracy < 60)
    const strongFamilies = allFamilies.filter((f) => f.accuracy >= 80)
    const questionsPerFamily = Math.max(
      2,
      Math.round(totalQuestions / Math.max(1, allFamilies.length)),
    )

    const byFamily = buildFamilyPerformance(allFamilies, questionsPerFamily)
    const avgTime =
      modules.reduce((s, m) => s + m.avgTimePerQuestion, 0) /
      Math.max(1, modules.length)

    return {
      attemptId,
      blueprintId,
      completedAt,
      selectedGroup: group,
      score: { correctCount, totalQuestions, percentage: scorePercent },
      time: {
        totalSeconds: Math.round(totalQuestions * avgTime),
        avgPerQuestion: Math.round(avgTime),
      },
      byModule: modules.map((m) => {
        const mQuestions = Math.round(totalQuestions / modules.length)
        const mCorrect = Math.round((m.score / 100) * mQuestions)
        return {
          moduleId: m.id as ModuleId,
          totalQuestions: mQuestions,
          correctCount: mCorrect,
          accuracy: m.score,
          avgTimePerQuestion: m.avgTimePerQuestion,
          estimatedLevelCode: m.levelCode,
          scorePercentage: m.score,
        }
      }),
      byTier: buildTierPerformance(totalQuestions, scorePercent),
      byFamily,
      weakestConcepts: byFamily
        .filter((f) => f.accuracy < 60)
        .sort((a, b) => a.accuracy - b.accuracy)
        .slice(0, 5),
      strongestConcepts: byFamily
        .filter((f) => f.accuracy >= 80)
        .sort((a, b) => b.accuracy - a.accuracy)
        .slice(0, 5),
      weakAreas: modules.filter((m) => m.score < 70).map((m) => m.title),
      strongAreas: modules.filter((m) => m.score >= 80).map((m) => m.title),
      suggestedPractice: buildSuggestedPractice(modules),
      recommendation: buildRecommendation(scorePercent, weakFamilies),
      estimatedLevels: modules.map((m) => ({
        moduleId: m.id as ModuleId,
        moduleTitle: m.title,
        estimatedTier: m.estimatedTier,
        estimatedLevelCode: m.levelCode,
        expectedTier: m.expectedTier,
        expectedLevelCode: overallLevelCode,
        delta: m.estimatedTier - m.expectedTier,
        deltaLabel: (m.estimatedTier > m.expectedTier
          ? 'boven'
          : m.estimatedTier === m.expectedTier
            ? 'op'
            : 'onder') as 'boven' | 'op' | 'onder',
        questionCount: Math.round(totalQuestions / modules.length),
        accuracy: m.score,
      })),
      overallEstimatedTier: overallTier,
      overallEstimatedLevelCode: overallLevelCode,
      overallExpectedTier: expectedTier,
      overallExpectedLevelCode: overallLevelCode,
      growthIndicators: buildGrowthIndicators(modules),
      strengthsBySkill: buildSkillGroupedAreas(modules, 'strong'),
      focusAreasBySkill: buildSkillGroupedAreas(modules, 'weak'),
      actionPlan: buildActionPlan(modules),
    }
  }

  // Helper to create blueprint and attempt
  const createAssessmentData = async (
    learnerId: string,
    domainId: string,
    group: number,
    daysAgo: number,
    scorePercent: number,
    overallLevelCode: string,
    overallTier: number,
    expectedTier: number,
    modules: DemoModuleConfig[],
  ) => {
    if (!learnerId) return

    const totalQuestions = 30
    const idSuffix = `${learnerId}_${domainId}_${daysAgo}`
    const blueprintId = `demo_bp_${idSuffix}`
    const attemptId = `demo_att_${idSuffix}`
    const completedAt = now - daysAgo * dayMs

    const blueprint: AssessmentBlueprint = {
      id: blueprintId,
      createdAt: completedAt - 30 * 60 * 1000,
      selectedGroup: group,
      config: {
        questionCount: totalQuestions,
        moduleFilter: modules.map((m) => m.id as ModuleId),
        minModules: 1,
        countsForMastery: true,
        mode: 'general',
        domainId: domainId as AssessmentDomainId,
      },
      selectionPlan: {
        weakPercentage: 30,
        mediumPercentage: 40,
        strongPercentage: 30,
        actualDistribution: { weak: 9, medium: 12, strong: 9 },
        tierDistribution: {
          targetPct: { low: 35, mid: 40, high: 25 },
          actual: { low: 10, mid: 12, high: 8 },
          availableFamilies: { low: 8, mid: 9, high: 3 },
        },
        strategy: 'tier-balanced',
        citoDifficultyStats: {
          withDifficulty: 24,
          withoutDifficulty: 6,
          averageDifficulty: 0.42,
          progressiveOrdering: true,
        },
      },
      questions: [],
      masterySnapshot: {
        totalFamilies: 20,
        weakFamilies: 4,
        mediumFamilies: 10,
        strongFamilies: 6,
      },
      eligibilityFilter: 'demo',
      pickedQuestionIds: [],
    }

    const avgTime =
      modules.reduce((s, m) => s + m.avgTimePerQuestion, 0) /
      Math.max(1, modules.length)

    const attempt: AssessmentAttempt = {
      id: attemptId,
      blueprintId,
      startedAt: completedAt - Math.round(totalQuestions * avgTime * 1000),
      completedAt,
      selectedGroup: group,
      learnerId,
      countsForMastery: true,
      totalTimeSeconds: Math.round(totalQuestions * avgTime),
      status: 'completed',
      domainId: domainId as AssessmentDomainId,
      answers: Array(totalQuestions)
        .fill(null)
        .map((_, i) => ({
          questionId: `q_${i}`,
          userChoice: 0,
          isCorrect: i < (totalQuestions * scorePercent) / 100,
          timeSpent: Math.round(avgTime + (Math.random() - 0.5) * 20),
          familyId:
            modules[i % modules.length].families[
              i % modules[i % modules.length].families.length
            ]?.id || 'demo_family',
          module: modules[i % modules.length].id as ModuleId,
        })),
    }

    const report = createReport(
      attemptId,
      blueprintId,
      learnerId,
      group,
      completedAt,
      scorePercent,
      totalQuestions,
      domainId,
      modules,
      overallLevelCode,
      overallTier,
      expectedTier,
    )

    try {
      await dataLayer.saveAssessmentBlueprint(blueprintId, blueprint)
      await dataLayer.saveAssessmentAttempt(attemptId, attempt)
      await dataLayer.saveAssessmentReport(attemptId, report)
    } catch (e) {
      debug('Failed to save demo assessment data for', learnerId, e)
    }
  }

  // ─── Seed Data ─────────────────────────────────────────────────────────────

  // Djayden (Group 7) — Rekenen: strong overall, weak in breuken
  await createAssessmentData(djayden, 'rekenen', 7, 14, 78, 'M7+', 12, 11, [
    {
      id: 'Basisrekenen',
      title: 'Basisrekenen',
      score: 90,
      levelCode: 'M7+',
      estimatedTier: 13,
      expectedTier: 11,
      avgTimePerQuestion: 32,
      families: [
        {
          id: 'REK-CIJF-PLUS',
          description: 'Cijferend optellen',
          skill: 'Cijferend rekenen',
          accuracy: 95,
          preMastery: 70,
          postMastery: 88,
        },
        {
          id: 'REK-CIJF-MIN',
          description: 'Cijferend aftrekken',
          skill: 'Cijferend rekenen',
          accuracy: 88,
          preMastery: 65,
          postMastery: 82,
        },
        {
          id: 'REK-CIJF-KEER',
          description: 'Cijferend vermenigvuldigen',
          skill: 'Cijferend rekenen',
          accuracy: 85,
          preMastery: 55,
          postMastery: 78,
        },
        {
          id: 'REK-TAFELS-BASIS',
          description: 'Tafels van vermenigvuldiging',
          skill: 'Tafels',
          accuracy: 92,
          preMastery: 80,
          postMastery: 90,
        },
      ],
    },
    {
      id: 'Getalbegrip',
      title: 'Getalbegrip',
      score: 68,
      levelCode: 'M7',
      estimatedTier: 11,
      expectedTier: 11,
      avgTimePerQuestion: 48,
      families: [
        {
          id: 'GET-BREUK-BASIS',
          description: 'Breuken herkennen',
          skill: 'Breuken',
          accuracy: 55,
          preMastery: 40,
          postMastery: 52,
        },
        {
          id: 'GET-BREUK-LEZEN',
          description: 'Breuken lezen en schrijven',
          skill: 'Breuken',
          accuracy: 50,
          preMastery: 35,
          postMastery: 48,
        },
        {
          id: 'GET-VERGELIJK',
          description: 'Getallen vergelijken',
          skill: 'Getalbegrip',
          accuracy: 80,
          preMastery: 60,
          postMastery: 75,
        },
        {
          id: 'GET-ROND-BASIS',
          description: 'Afronden',
          skill: 'Getalbegrip',
          accuracy: 85,
          preMastery: 65,
          postMastery: 80,
        },
      ],
    },
  ])

  // Djayden — Taalverzorging: moderate, weak in werkwoordspelling
  await createAssessmentData(
    djayden,
    'taalverzorging',
    7,
    7,
    65,
    'M7',
    11,
    11,
    [
      {
        id: 'SpellingWW',
        title: 'Werkwoordspelling',
        score: 55,
        levelCode: 'B7+',
        estimatedTier: 10,
        expectedTier: 11,
        avgTimePerQuestion: 38,
        families: [
          {
            id: 'SWW-TT-STAM',
            description: 'Stam en stam+t herkennen',
            skill: 'Tegenwoordige tijd',
            accuracy: 60,
            preMastery: 45,
            postMastery: 55,
          },
          {
            id: 'SWW-TT-DSTAM',
            description: 'D-stam werkwoorden',
            skill: 'Tegenwoordige tijd',
            accuracy: 45,
            preMastery: 30,
            postMastery: 40,
          },
          {
            id: 'SWW-VT-ZWAK-KOF',
            description: 'Verleden tijd zwak (kofschip)',
            skill: 'Verleden tijd',
            accuracy: 50,
            preMastery: 35,
            postMastery: 45,
          },
          {
            id: 'SWW-VD-KOF',
            description: 'Voltooid deelwoord (kofschip)',
            skill: 'Voltooid deelwoord',
            accuracy: 65,
            preMastery: 50,
            postMastery: 60,
          },
        ],
      },
      {
        id: 'SpellingRegel',
        title: 'Regelspelling',
        score: 75,
        levelCode: 'M7',
        estimatedTier: 11,
        expectedTier: 11,
        avgTimePerQuestion: 28,
        families: [
          {
            id: 'SPR-KLANK-AA-EE-OO',
            description: 'Lange klanken schrijven',
            skill: 'Klanken',
            accuracy: 82,
            preMastery: 60,
            postMastery: 78,
          },
          {
            id: 'SPR-KLANK-BAKKER',
            description: 'Medeklinkerverdubbeling',
            skill: 'Klanken',
            accuracy: 78,
            preMastery: 55,
            postMastery: 72,
          },
          {
            id: 'SPR-LEEN-FRANS',
            description: 'Franse leenwoorden',
            skill: 'Leenwoorden',
            accuracy: 60,
            preMastery: 40,
            postMastery: 55,
          },
          {
            id: 'SPR-WEET-EI-IJ',
            description: 'Ei of ij',
            skill: 'Weetwoorden',
            accuracy: 80,
            preMastery: 65,
            postMastery: 76,
          },
        ],
      },
    ],
  )

  // Sophie (Group 8) — Taalverzorging: excellent
  await createAssessmentData(sophie, 'taalverzorging', 8, 5, 92, 'E8', 16, 14, [
    {
      id: 'SpellingWW',
      title: 'Werkwoordspelling',
      score: 95,
      levelCode: 'E8+',
      estimatedTier: 16,
      expectedTier: 14,
      avgTimePerQuestion: 22,
      families: [
        {
          id: 'SWW-TT-STAM',
          description: 'Stam en stam+t herkennen',
          skill: 'Tegenwoordige tijd',
          accuracy: 98,
          preMastery: 85,
          postMastery: 95,
        },
        {
          id: 'SWW-VT-STERK',
          description: 'Sterke werkwoorden verleden tijd',
          skill: 'Verleden tijd',
          accuracy: 90,
          preMastery: 75,
          postMastery: 88,
        },
        {
          id: 'SWW-VD-KOF',
          description: 'Voltooid deelwoord (kofschip)',
          skill: 'Voltooid deelwoord',
          accuracy: 95,
          preMastery: 80,
          postMastery: 92,
        },
      ],
    },
    {
      id: 'SpellingRegel',
      title: 'Regelspelling',
      score: 88,
      levelCode: 'E8',
      estimatedTier: 15,
      expectedTier: 14,
      avgTimePerQuestion: 20,
      families: [
        {
          id: 'SPR-KLANK-AA-EE-OO',
          description: 'Lange klanken schrijven',
          skill: 'Klanken',
          accuracy: 92,
          preMastery: 80,
          postMastery: 90,
        },
        {
          id: 'SPR-LEEN-FRANS',
          description: 'Franse leenwoorden',
          skill: 'Leenwoorden',
          accuracy: 82,
          preMastery: 65,
          postMastery: 80,
        },
        {
          id: 'SPR-LEEN-ENGELS',
          description: 'Engelse leenwoorden',
          skill: 'Leenwoorden',
          accuracy: 88,
          preMastery: 70,
          postMastery: 85,
        },
      ],
    },
  ])

  // Emma (Group 7) — Rekenen: struggling, needs attention
  await createAssessmentData(emma, 'rekenen', 7, 3, 52, 'B7', 9, 11, [
    {
      id: 'Basisrekenen',
      title: 'Basisrekenen',
      score: 58,
      levelCode: 'B7+',
      estimatedTier: 10,
      expectedTier: 11,
      avgTimePerQuestion: 55,
      families: [
        {
          id: 'REK-CIJF-PLUS',
          description: 'Cijferend optellen',
          skill: 'Cijferend rekenen',
          accuracy: 70,
          preMastery: 50,
          postMastery: 60,
        },
        {
          id: 'REK-CIJF-MIN',
          description: 'Cijferend aftrekken',
          skill: 'Cijferend rekenen',
          accuracy: 55,
          preMastery: 40,
          postMastery: 48,
        },
        {
          id: 'REK-CIJF-KEER',
          description: 'Cijferend vermenigvuldigen',
          skill: 'Cijferend rekenen',
          accuracy: 40,
          preMastery: 30,
          postMastery: 35,
        },
        {
          id: 'REK-CIJF-DEEL',
          description: 'Cijferend delen',
          skill: 'Cijferend rekenen',
          accuracy: 35,
          preMastery: 25,
          postMastery: 30,
        },
        {
          id: 'REK-TAFELS-BASIS',
          description: 'Tafels van vermenigvuldiging',
          skill: 'Tafels',
          accuracy: 75,
          preMastery: 60,
          postMastery: 68,
        },
      ],
    },
    {
      id: 'Getalbegrip',
      title: 'Getalbegrip',
      score: 45,
      levelCode: 'B7',
      estimatedTier: 9,
      expectedTier: 11,
      avgTimePerQuestion: 62,
      families: [
        {
          id: 'GET-BREUK-BASIS',
          description: 'Breuken herkennen',
          skill: 'Breuken',
          accuracy: 35,
          preMastery: 25,
          postMastery: 30,
        },
        {
          id: 'GET-BREUK-LEZEN',
          description: 'Breuken lezen en schrijven',
          skill: 'Breuken',
          accuracy: 30,
          preMastery: 20,
          postMastery: 25,
        },
        {
          id: 'GET-VERGELIJK',
          description: 'Getallen vergelijken',
          skill: 'Getalbegrip',
          accuracy: 60,
          preMastery: 45,
          postMastery: 52,
        },
        {
          id: 'GET-ROND-BASIS',
          description: 'Afronden',
          skill: 'Getalbegrip',
          accuracy: 55,
          preMastery: 40,
          postMastery: 48,
        },
      ],
    },
  ])

  // Noah (Group 7) — Rekenen: solid average
  await createAssessmentData(noah, 'rekenen', 7, 10, 75, 'M7', 11, 11, [
    {
      id: 'Basisrekenen',
      title: 'Basisrekenen',
      score: 78,
      levelCode: 'M7',
      estimatedTier: 11,
      expectedTier: 11,
      avgTimePerQuestion: 35,
      families: [
        {
          id: 'REK-CIJF-PLUS',
          description: 'Cijferend optellen',
          skill: 'Cijferend rekenen',
          accuracy: 85,
          preMastery: 60,
          postMastery: 78,
        },
        {
          id: 'REK-CIJF-MIN',
          description: 'Cijferend aftrekken',
          skill: 'Cijferend rekenen',
          accuracy: 75,
          preMastery: 55,
          postMastery: 70,
        },
        {
          id: 'REK-HANDIG-PLUS-MIN',
          description: 'Handig optellen en aftrekken',
          skill: 'Handig rekenen',
          accuracy: 72,
          preMastery: 50,
          postMastery: 65,
        },
      ],
    },
    {
      id: 'Getalbegrip',
      title: 'Getalbegrip',
      score: 72,
      levelCode: 'M7',
      estimatedTier: 11,
      expectedTier: 11,
      avgTimePerQuestion: 40,
      families: [
        {
          id: 'GET-BREUK-BASIS',
          description: 'Breuken herkennen',
          skill: 'Breuken',
          accuracy: 70,
          preMastery: 50,
          postMastery: 65,
        },
        {
          id: 'GET-VERGELIJK',
          description: 'Getallen vergelijken',
          skill: 'Getalbegrip',
          accuracy: 75,
          preMastery: 55,
          postMastery: 70,
        },
      ],
    },
  ])

  // Liam (Group 8) — Rekenen: below expected, needs support
  await createAssessmentData(liam, 'rekenen', 8, 7, 48, 'B8', 12, 14, [
    {
      id: 'Basisrekenen',
      title: 'Basisrekenen',
      score: 52,
      levelCode: 'B8',
      estimatedTier: 12,
      expectedTier: 14,
      avgTimePerQuestion: 58,
      families: [
        {
          id: 'REK-CIJF-PLUS',
          description: 'Cijferend optellen',
          skill: 'Cijferend rekenen',
          accuracy: 65,
          preMastery: 50,
          postMastery: 55,
        },
        {
          id: 'REK-CIJF-KEER',
          description: 'Cijferend vermenigvuldigen',
          skill: 'Cijferend rekenen',
          accuracy: 40,
          preMastery: 60,
          postMastery: 42,
        },
        {
          id: 'REK-CIJF-DEEL',
          description: 'Cijferend delen',
          skill: 'Cijferend rekenen',
          accuracy: 35,
          preMastery: 55,
          postMastery: 38,
        },
        {
          id: 'REK-CONTEXT-MEERSTAPS',
          description: 'Meerstapsproblemen',
          skill: 'Contextrekenen',
          accuracy: 30,
          preMastery: 45,
          postMastery: 32,
        },
      ],
    },
    {
      id: 'Getalbegrip',
      title: 'Getalbegrip',
      score: 44,
      levelCode: 'B8-',
      estimatedTier: 11,
      expectedTier: 14,
      avgTimePerQuestion: 65,
      families: [
        {
          id: 'GET-PROC-BASIS',
          description: 'Procenten berekenen',
          skill: 'Procenten',
          accuracy: 38,
          preMastery: 30,
          postMastery: 35,
        },
        {
          id: 'GET-BREUK-GEMENGD',
          description: 'Gemengde breuken',
          skill: 'Breuken',
          accuracy: 42,
          preMastery: 35,
          postMastery: 38,
        },
        {
          id: 'GET-KOMMA-PLUS',
          description: 'Kommagetallen optellen',
          skill: 'Kommagetallen',
          accuracy: 50,
          preMastery: 40,
          postMastery: 45,
        },
      ],
    },
  ])

  // Noor (Group 8) — Begrijpend lezen: good
  await createAssessmentData(
    noor,
    'begrijpend-lezen',
    8,
    4,
    82,
    'M8+',
    15,
    14,
    [
      {
        id: 'Taalbegrip',
        title: 'Taalbegrip',
        score: 82,
        levelCode: 'M8+',
        estimatedTier: 15,
        expectedTier: 14,
        avgTimePerQuestion: 42,
        families: [
          {
            id: 'TAA-LEZEN-KERN',
            description: 'Kernzin herkennen',
            skill: 'Begrijpend lezen',
            accuracy: 88,
            preMastery: 70,
            postMastery: 85,
          },
          {
            id: 'TAA-LEZEN-DETAIL',
            description: 'Detailvragen beantwoorden',
            skill: 'Begrijpend lezen',
            accuracy: 85,
            preMastery: 65,
            postMastery: 80,
          },
          {
            id: 'TAA-LEZEN-CONCLUSIE',
            description: 'Conclusies trekken',
            skill: 'Begrijpend lezen',
            accuracy: 78,
            preMastery: 55,
            postMastery: 72,
          },
          {
            id: 'TAA-LEZEN-STRUCTUUR',
            description: 'Tekststructuur herkennen',
            skill: 'Tekstanalyse',
            accuracy: 72,
            preMastery: 50,
            postMastery: 68,
          },
          {
            id: 'TAA-SIGNAALWOORD',
            description: 'Signaalwoorden herkennen',
            skill: 'Tekstanalyse',
            accuracy: 80,
            preMastery: 60,
            postMastery: 76,
          },
        ],
      },
    ],
  )

  // ─── Teacher Assignment ("Toets Klaargezet") ──────────────────────────────
  if (teacherId) {
    const assignmentId = `demo_assignment_rek_g7`
    const assignment = {
      id: assignmentId,
      teacherId,
      classroomId: classroomId || 'demo_classroom',
      domainId: 'rekenen' as const,
      domainLabel: 'Rekenen',
      group: 7 as const,
      questionCount: 15,
      estimatedMinutes: 20,
      targetStudentIds: [],
      wholeClass: true,
      selectedFamilyIds: [
        'REK-CIJF-PLUS',
        'REK-CIJF-MIN',
        'REK-CIJF-KEER',
        'GET-BREUK-BASIS',
        'GET-BREUK-LEZEN',
      ],
      type: 'targeted' as const,
      selectedSkillSummary: 'cijferend rekenen, breuken basis',
      createdAt: now - 2 * 24 * 60 * 60 * 1000,
      deadline: now + 5 * 24 * 60 * 60 * 1000,
      status: 'active' as const,
      completedByStudentIds: [],
    }
    try {
      await dataLayer.saveTeacherAssignment(assignmentId, assignment)
      debug('✅ Demo teacher assignment (toets klaargezet) seeded')
    } catch (e) {
      debug('Failed to save teacher assignment', e)
    }
  }

  debug('✅ Demo assessment data seeded successfully')
}
